import React, { useState, useRef, useEffect } from "react";
import { getApi } from "../../Services/apiService";
import { toast } from "react-toastify";
import { formatSuffix, getShortDisplayName } from "../../Utils/name";
import { renderNameWithColor } from "../../Utils/colors";
import { getFamilyTreeIdFromStorage } from "../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../Utils/dashboardMemberDisplayName";
import {
  getMemberAgeAtYearEnd,
  getMemberBirthCalendarParts,
  getMemberDeathYear,
} from "../../Utils/family-tree/dateAndTime";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../Utils/dashboardMultipleBirthSuperscript";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../Utils/dashboardStartOfLineMember";
import DashboardMarkerLegend from "./DashboardComponents/DashboardMarkerLegend";

const REPORT_CATEGORIES = [
  {
    category: "Age",
    items: [
      { value: "age-18-and-over", label: "18 and Older", endpoint: "/reports/adult-family-members" },
      { value: "age-by-generation-and-age", label: "By Generation and Age", endpoint: "/reports/family-by-generation-age-order" },
      { value: "age-by-groups", label: "By Groups", endpoint: "/reports/age-groups-of-descendants" },
      { value: "age-order", label: "Order", endpoint: "/reports/family-in-age-order" },
    ],
  },
  {
    category: "Birthdays",
    items: [
      { value: "birthdays-by-birth-day", label: "By Birth Day", endpoint: "/reports/birthdays-by-birth-day" },
      { value: "birthdays-by-inlaws", label: "By In-Laws", endpoint: "/reports/birthdays-by-inlaws" },
      { value: "birthdays-by-lines", label: "By Lines", endpoint: "/reports/birthdays-by-lines" },
      { value: "birthdays-by-milestone-ages", label: "By Milestone Ages", endpoint: "/reports/birthdays-by-milestone-ages" },
      { value: "birthdays-by-milestone-month", label: "By Milestone Month", endpoint: "/reports/birthdays-by-milestone-month" },
      { value: "birthdays-by-month-day", label: "By Month/Day", endpoint: "/reports/birthdays-by-month-day" },
    ],
  },
  {
    category: "Deceased*",
    items: [
      { value: "deceased-by-month", label: "By Month", endpoint: "/reports/deceased-by-month" },
      { value: "deceased-by-year", label: "By Year", endpoint: "/reports/deceased-by-year" },
    ],
  },
  {
    category: "Generations",
    items: [
      { value: "generations-by-age-order", label: "By Age Order", endpoint: "/reports/generations-by-age-order" },
      { value: "generations-by-gender", label: "By Gender", endpoint: "/reports/generations-by-gender" },
    ],
  },
  {
    category: "Marriages*",
    isMissing: true,
    items: [
      { value: "marriages-by-milestone-wed-anniv", label: "By Milestone Wedding Anniversary", endpoint: "/reports/marriages-by-milestone-wed-anniv" },
      { value: "marriages-by-month", label: "By Month", endpoint: "/reports/marriages-by-month" },
      { value: "marriages-by-time", label: "By Time", endpoint: "/reports/marriages-by-time" },
    ],
  },
  {
    category: "Misc Reports",
    items: [
      { value: "misc-by-first-names", label: "By First Names", endpoint: "/reports/misc-by-first-names" },
      { value: "misc-by-gender", label: "By Gender", endpoint: "/reports/misc-by-gender" },
      { value: "misc-children-by-desc-parents", label: "Children by Descendant Parents", endpoint: "/reports/misc-children-by-desc-parents" },
      { value: "misc-family-by-states", label: "Family by States", endpoint: "/reports/misc-family-by-states" },
      { value: "misc-family-members-since-2000", label: "Family Members (Since 2000)", endpoint: "/report-management/family-members-by-year-of-birth" },
      { value: "misc-family-head-grands", label: "Grands by Family Head", endpoint: "/reports/misc-family-head-grands" },
      { value: "misc-grands-by-top-of-line", label: "Grands by Top of Line", endpoint: "/reports/misc-top-of-line-grands" },
      { value: "misc-lines-by-color", label: "Lines by Color", endpoint: "/reports/misc-lines-by-color" },
      { value: "misc-multiple-births-by-yr-gen", label: "Multiple Births by Year/Generation", endpoint: "/reports/misc-multiple-birth-count" },
      { value: "misc-new-fam-since-2000-current", label: "New Family Since 2000 - Current", endpoint: "/reports/misc-new-family-members-since-2000" },
    ],
  },
];

/** First birth year the "Family Members (since 2000)" report asks for. */
const FAMILY_MEMBERS_SINCE_YEAR = 2000;

export default function Reports() {
  const [selectedReportType, setSelectedReportType] = useState("");
  const [selectedReportEndpoint, setSelectedReportEndpoint] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Find selected label from categories
  const getSelectedLabel = () => {
    if (!selectedReportType) return "Select Report Type";
    
    for (const category of REPORT_CATEGORIES) {
      const item = category.items.find(item => item.value === selectedReportType);
      if (item) {
        return `${category.category} - ${item.label}`;
      }
    }
    return "Select Report Type";
  };

  const handleSelect = async (value, endpoint) => {
    setSelectedReportType(value);
    setSelectedReportEndpoint(endpoint);
    setIsDropdownOpen(false);
    setError(null);
    setReportData(null);
    
    // Fetch report data
    if (endpoint) {
      await fetchReportData(endpoint, value);
    }
  };

  const fetchReportData = async (endpoint, reportValue) => {
    try {
      setLoading(true);
      setError(null);
      
      const family_tree_id = getFamilyTreeIdFromStorage();

      if (!family_tree_id) {
        setError("Family tree ID not found. Please login again.");
        setLoading(false);
        return;
      }

      // Build query parameters
      let queryParams = `family_tree_id=${family_tree_id}`;
      
      // Add gender parameter for gender-by-generation report
      if (reportValue === "gender-by-generation") {
        // Default to "Everyone" (gender=4) - can be made configurable later
        queryParams += `&gender=4`;
      }

      // "Family Members (since 2000)" reads the year-of-birth endpoint from
      // 2000 to the current year; without the filter it would return every year
      // the tree holds.
      if (reportValue === "misc-family-members-since-2000") {
        queryParams += `&start_year=${FAMILY_MEMBERS_SINCE_YEAR}`;
      }

      // Make API call
      const response = await getApi(`${endpoint}?${queryParams}`);
      
      if (response?.statusCode === 200) {
        setReportData(response?.data || response);
        // toast.success("Report data loaded successfully");
      } else {
        setError(response?.message || "Failed to load report data");
        toast.error(response?.message || "Failed to load report data");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "An error occurred while fetching report data";
      setError(errorMessage);
      toast.error(errorMessage);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Same name format as Total Family Members: "FirstName, Suffix." or "FirstName SpecialName" or "FirstName"
  const formatPersonName = (member) => {
    const firstName = member?.first_name || member?.name?.split?.(" ")?.[0] || "Unnamed";
    const suffix = member?.suffix || "";
    const specialName = member?.special_name || "";
    if (suffix) {
      return `${firstName}, ${formatSuffix(suffix)}`;
    }
    if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const getHeadOfLineName = formatDashboardFamilyLineHeadName;

  const getHeadOfLineNameFromGroup = formatDashboardFamilyLineHeadName;

  const isAdultFamilyMembersReport =
    selectedReportType === "age-18-and-over" &&
    reportData &&
    Array.isArray(reportData.members);

  const sortedAdultMembers = isAdultFamilyMembersReport
    ? [...reportData.members].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      )
    : [];

  const isGenerationAgeReport =
    selectedReportType === "age-by-generation-and-age" &&
    reportData &&
    Array.isArray(reportData.generations);

  const isAgeGroupsReport =
    selectedReportType === "age-by-groups" &&
    reportData &&
    Array.isArray(reportData.age_groups);

  const isAgeOrderReport =
    selectedReportType === "age-order" &&
    reportData &&
    Array.isArray(reportData.members);

  const isDeceasedByMonthReport =
    selectedReportType === "deceased-by-month" &&
    reportData &&
    (Array.isArray(reportData.months) || Array.isArray(reportData.month_groups) || Array.isArray(reportData?.data?.months));

  const isDeceasedByYearReport =
    selectedReportType === "deceased-by-year" &&
    reportData &&
    (Array.isArray(reportData.years) || Array.isArray(reportData.year_groups) || Array.isArray(reportData?.data?.years));

  const deceasedByMonthMonths = isDeceasedByMonthReport
    ? (reportData.months || reportData.month_groups || reportData?.data?.months || []).map((m) => ({
        month: m.month ?? m.month_number,
        count: m.total_count ?? m.count ?? (m.members?.length ?? 0),
        members: (m.members || []).sort((a, b) => (a.death_day ?? 0) - (b.death_day ?? 0)),
      }))
    : [];

  const deceasedByYearGroups = isDeceasedByYearReport
    ? (reportData.years || reportData.year_groups || reportData?.data?.years || []).map((y) => ({
        year: y.year ?? y.death_year ?? y.year_number,
        count: y.total_count ?? y.count ?? (y.members?.length ?? 0),
        members: (y.members || []).sort((a, b) => {
          const monthA = a.death_month ?? a.month ?? 0;
          const monthB = b.death_month ?? b.month ?? 0;
          if (monthA !== monthB) return monthA - monthB;
          return (a.death_day ?? 0) - (b.death_day ?? 0);
        }),
      }))
    : [];

  const isFamilyMembersByYearReport =
    selectedReportType === "misc-family-members-since-2000" &&
    reportData &&
    (Array.isArray(reportData.years) || Array.isArray(reportData?.data?.years));

  // Same grouping as the birthdays lists, read against the birth year instead
  // of the birth month: each year heads its own block and the rows inside it
  // run in calendar order, so a year reads January through December.
  const familyMembersByYearGroups = isFamilyMembersByYearReport
    ? (reportData.years || reportData?.data?.years || []).map((y) => ({
        year: y.year ?? y.birth_year,
        count: y.total_count ?? y.count ?? (y.members?.length ?? 0),
        members: [...(y.members || [])].sort((a, b) => {
          const partsA = getMemberBirthCalendarParts(a);
          const partsB = getMemberBirthCalendarParts(b);
          const monthA = partsA ? partsA.month : 12;
          const monthB = partsB ? partsB.month : 12;
          if (monthA !== monthB) return monthA - monthB;
          return (partsA?.day ?? 32) - (partsB?.day ?? 32);
        }),
      }))
    : [];

  const familyMembersSinceYear =
    (isFamilyMembersByYearReport
      ? reportData.start_year ?? reportData?.data?.start_year
      : null) ?? FAMILY_MEMBERS_SINCE_YEAR;

  const topOfLineName = isGenerationAgeReport && reportData?.top_of_line
    ? typeof reportData.top_of_line === "string"
      ? String(reportData.top_of_line).toUpperCase()
      : (
          reportData.top_of_line.full_name ||
          [reportData.top_of_line.first_name, reportData.top_of_line.last_name].filter(Boolean).join(" ") ||
          "Top of Line"
        ).toUpperCase()
    : "";

  // The reports this page lays out as rows (everything but the raw JSON
  // fallback) each carry the marker legend, keyed to the rows they hold.
  const isLaidOutReport =
    isAgeGroupsReport ||
    isGenerationAgeReport ||
    isAgeOrderReport ||
    isDeceasedByMonthReport ||
    isDeceasedByYearReport ||
    isFamilyMembersByYearReport ||
    isAdultFamilyMembersReport;

  // Every row of the current report, whatever shape the API grouped it in, for
  // the legend's multiple-birth tags. getMultipleBirthLegendTags walks nested
  // arrays itself, so the groups only need flattening one level.
  const reportLegendMembers = isAgeGroupsReport
    ? (reportData.age_groups || []).map((group) => group.members || [])
    : isGenerationAgeReport
      ? (reportData.generations || []).map((gen) =>
          Array.isArray(gen.members)
            ? gen.members
            : (gen.color_groups || []).map((cg) => cg.members || [])
        )
      : isAgeOrderReport
        ? reportData.members || []
        : isDeceasedByMonthReport
          ? deceasedByMonthMonths.map((m) => m.members)
          : isDeceasedByYearReport
            ? deceasedByYearGroups.map((y) => y.members)
            : isFamilyMembersByYearReport
              ? familyMembersByYearGroups.map((y) => y.members)
              : sortedAdultMembers;

  /** Members for By Generation and Age: new API uses gen.members; legacy uses color_groups */
  const getGenAgeMembersSorted = (gen) => {
    const hasFlatMembers = Array.isArray(gen?.members);
    if (hasFlatMembers) {
      return [...gen.members].sort((a, b) => (b.age ?? 0) - (a.age ?? 0));
    }
    const flat = (gen.color_groups || []).flatMap((cg) =>
      (cg.members || []).map((m) => ({ ...m, _cg: cg }))
    );
    return flat.sort((a, b) => (b.age ?? 0) - (a.age ?? 0));
  };

  const lineGroupForGenAgeMember = (member, generationLevel) => {
    if (generationLevel >= 3) {
      return member.head_of_line || member.top_of_line || "—";
    }
    if (generationLevel === 2) {
      return member.top_of_line || member.head_of_line || "—";
    }
    return member.top_of_line || member.head_of_line || "—";
  };

  /** Same head display as 18+ report: head_first_name/suffix/special, else normalize API line strings */
  const headNameForGenAgeLineColumn = (member, generationLevel) => {
    const fromFields = getHeadOfLineName(member);
    if (fromFields) return fromFields;
    const raw = lineGroupForGenAgeMember(member, generationLevel);
    if (!raw || raw === "—") return "";
    const t = String(raw).trim();
    if (t.includes("&")) return t;
    const romanFirst = t.match(/^(III|II|IV)\s+(\S+)/i);
    if (romanFirst) {
      return getShortDisplayName(romanFirst[2], romanFirst[1], "");
    }
    const srjr = t.match(/^(Sr\.?|Jr\.?)\s+(\S+)/i);
    if (srjr) {
      const suf = srjr[1].replace(/\.$/, "");
      return getShortDisplayName(srjr[2], suf, "");
    }
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2 && parts[1].length <= 2 && /^[A-Za-z]\.?$/i.test(parts[1])) {
      return `${parts[0]} ${parts[1].replace(/\.$/, "")}`;
    }
    if (parts.length >= 2) return parts[0];
    return t;
  };

  // The year-of-birth endpoint names its in-laws is_spouse; every other report
  // on this page sends is_in_laws. Both mean the same asterisk.
  const isReportInLawMember = (member) =>
    member?.is_in_laws === true ||
    member?.is_in_laws === "true" ||
    member?.is_in_laws === 1 ||
    member?.is_spouse === true ||
    member?.is_spouse === "true" ||
    member?.is_spouse === 1;

  // The markers after a name, in the order the legend keys them: angel for the
  // deceased, asterisk for an in-law, then the multiple-birth tag (twin / trip /
  // quad). One snippet for every row shape on this page, so a report never prints
  // a marker its legend does not explain — or the other way round.
  const renderReportNameMarkers = (member, { showInLawAsterisk }) => {
    const isDeceased = member?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    return (
      <>
        {isDeceased && (
          <span className="reports-adult-angel-icon">
            <img
              src={window.getPublicUrl("/assets/images/angel.png")}
              alt="deceased"
              style={{ width: "12px", height: "12px" }}
            />
          </span>
        )}
        {showInLawAsterisk && (
          <span className="reports-adult-in-law-asterisk">*</span>
        )}
        {multipleBirthSup && (
          <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
        )}
      </>
    );
  };

  // Shared row: same name format (formatPersonName + markers) and line/color column for all reports
  const renderReportMemberRow = (member, { headOfLineName, hexCode, colorName, key = member.id, showAge = false, age = null, displayName = null }) => {
    const personName = displayName != null ? displayName : formatPersonName(member);
    const isInLaw = isReportInLawMember(member);
    const head = headOfLineName ?? getHeadOfLineName(member);
    const hex = hexCode ?? member?.hex_code ?? "#000000";
    const color = colorName ?? member?.color_name ?? "";
    return (
      <div key={key} className="reports-adult-member-row">
        {showAge && (
          <span className="reports-gen-age-age-inline">{isInLaw ? "" : age ?? member?.age ?? ""}</span>
        )}
        <div className="reports-adult-member-name">
          <span>{personName}</span>
          {renderReportNameMarkers(member, {
            showInLawAsterisk:
              isInLaw && !isExplicitDashboardStartOrTopOfLineMember(member),
          })}
        </div>
        <div className="reports-adult-member-line">
          {renderNameWithColor(head || "Start/Top of Line", hex, color ? `${color} Line` : "")}
        </div>
      </div>
    );
  };

  const renderAdultMemberRow = (member) =>
    renderReportMemberRow(member, {
      headOfLineName: getHeadOfLineName(member),
      hexCode: member?.hex_code,
      colorName: member?.color_name,
    });

  /** Deceased row: Name | Year | Line | Time Deceased (optional day column for by-month) */
  const renderDeceasedMemberRow = (member, { showDay = false } = {}) => {
    const head = getHeadOfLineName(member) || "Start/Top of Line";
    const hex = member?.hex_code ?? "#000000";
    const color = member?.color_name ?? member?.color ?? "";
    const timeDec = member?.time_deceased ?? member?.years_deceased ?? member?.age_at_death ?? "—";
    const deathYear = getMemberDeathYear(member);
    return (
      <div key={member?.id} className={`reports-deceased-member-row${showDay ? "" : " reports-deceased-member-row--no-day"}`}>
        {showDay && (
          <span className="reports-deceased-cell reports-deceased-day">{member?.death_day ?? "—"}</span>
        )}
        <span className="reports-deceased-cell reports-deceased-name">
          {formatPersonName(member)}
          {renderReportNameMarkers(member, {
            showInLawAsterisk: isReportInLawMember(member),
          })}
        </span>
        <span className="reports-deceased-cell reports-deceased-year">{deathYear || "—"}</span>
        <div className="reports-deceased-cell reports-deceased-line">
          {renderNameWithColor(head, hex, color ? `${color} Line` : "")}
        </div>
        <span className="reports-deceased-cell reports-deceased-time">{timeDec}</span>
      </div>
    );
  };

  const renderDeceasedByMonthRow = (member) => renderDeceasedMemberRow(member, { showDay: true });

  /** Family Members (since 2000) row — the birthdays columns (Age | Name | Mo |
   *  Day | Line) under a year heading instead of a month one. */
  const renderFamilyMemberByYearRow = (member, key) => {
    const birthParts = getMemberBirthCalendarParts(member);
    const headShort = getHeadOfLineName(member);
    const age = member?.age ?? getMemberAgeAtYearEnd(member);
    const colorName = member?.color_name || "";
    const hex = member?.hex_code || "#000000";
    return (
      <div key={key} className="reports-gen-age-order-row">
        <span className="reports-gen-age-order-cell reports-gen-age-order-age">
          {age ?? ""}
        </span>
        <span className="reports-gen-age-order-cell reports-gen-age-order-name">
          {formatPersonName(member)}
          {renderReportNameMarkers(member, {
            showInLawAsterisk:
              isReportInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member),
          })}
        </span>
        <span className="reports-gen-age-order-cell reports-gen-age-order-mo">
          {birthParts ? birthParts.month + 1 : "—"}
        </span>
        <span className="reports-gen-age-order-cell reports-gen-age-order-day">
          {birthParts ? birthParts.day : "—"}
        </span>
        <div className="reports-gen-age-order-cell reports-gen-age-order-line-cell">
          {renderNameWithColor(
            headShort || "Start/Top of Line",
            hex,
            colorName ? `${colorName} Line` : ""
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container dashboard-detail-page reports-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <div className="reports-dropdown-wrapper">
              <label className="reports-dropdown-label">Type of Reports:</label>
              <div className="reports-custom-dropdown" ref={dropdownRef}>
                <button
                  type="button"
                  className="reports-dropdown-button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span>{getSelectedLabel()}</span>
                  <span className={`reports-dropdown-arrow ${isDropdownOpen ? "open" : ""}`}>
                    ▼
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="reports-dropdown-menu" role="listbox">
                    {REPORT_CATEGORIES.map((category) => (
                      <div key={category.category} className="reports-accordion-category">
                        <div
                          className="reports-accordion-header"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category.category);
                          }}
                        >
                          <span className="reports-category-name">
                            {category.category}
                          </span>
                          <span className={`reports-accordion-arrow ${expandedCategories[category.category] ? "open" : ""}`}>
                            ▼
                          </span>
                        </div>
                        {expandedCategories[category.category] && (
                          <div className="reports-accordion-content">
                            {category.items.map((item) => (
                              <div
                                key={item.value}
                                className={`reports-dropdown-item ${
                                  selectedReportType === item.value ? "selected" : ""
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(item.value, item.endpoint);
                                }}
                                role="option"
                                aria-selected={selectedReportType === item.value}
                              >
                                <span>{item.label}</span>
                                {item.note && (
                                  <span className="reports-item-note"> ({item.note})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Report Data Display Area */}
        {selectedReportType && (
          <div className="reports-content-area">
            {loading && (
              <div className="reports-loading">
                <p>Loading report data...</p>
              </div>
            )}
            
            {error && (
              <div className="reports-error">
                <p>{error}</p>
              </div>
            )}
            
            {!loading && !error && reportData && (
              <>
                {isAgeGroupsReport ? (
                  <div className="reports-gen-age">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">Descendants Only</span>
                      <span className="reports-gen-age-subtitle">Age Groups of Descendants</span>
                      <span className="reports-gen-age-year">{reportData.year ?? new Date().getFullYear()}</span>
                    </div>
                    <div className="reports-gen-age-body">
                      <div className="reports-gen-age-table-header">
                        <span className="reports-gen-age-col-gen">Age</span>
                        <span className="reports-gen-age-header-spacer" aria-hidden="true" />
                        <span className="reports-gen-age-col-count">Count</span>
                      </div>
                      {(reportData.age_groups || []).map((group) => (
                        <div key={group.age} className="reports-gen-age-section">
                          <div className="reports-gen-age-group-header">
                            <span className="reports-gen-age-badge">{group.age}</span>
                            <div className="reports-gen-age-divider" />
                            <span className="reports-gen-age-count-badge">{group.count ?? group.total_count ?? 0}</span>
                          </div>
                          <div className="reports-gen-age-members-container">
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {group.age}
                            </span>
                            <div className="reports-gen-age-list-scroll">
                              <div className="reports-gen-age-age-label">Age</div>
                              {(group.members || []).map((member, idx) =>
                                renderReportMemberRow(
                                  { ...member, id: member.id || `age-${group.age}-${idx}` },
                                  {
                                    headOfLineName: getHeadOfLineName(member) || "Start/Top of Line",
                                    hexCode: member.hex_code,
                                    colorName: member.color ?? member.color_name,
                                    key: member.id || `age-${group.age}-${idx}`,
                                    showAge: true,
                                    age: member.age,
                                  }
                                )
                              )}
                            </div>
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {group.count ?? group.total_count ?? 0}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : isGenerationAgeReport ? (
                  <div className="reports-gen-age">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">{reportData.title || "Descendants Only"}</span>
                      <span className="reports-gen-age-subtitle">{reportData.subtitle || "Family by Generation/Age Order"}</span>
                      <span className="reports-gen-age-year">{reportData.year ?? new Date().getFullYear()}</span>
                    </div>
                    <div className="reports-gen-age-body">
                      {reportData.generations?.some((g) => Array.isArray(g.members)) ? (
                        <div className="reports-gen-age-unified-header-wrap">
                          <div className="reports-gen-age-unified-header-single-line">
                            <span className="reports-gen-age-unified-gen">GEN</span>
                            <span className="reports-gen-age-unified-header-gutter" aria-hidden="true" />
                            <span className="reports-gen-age-unified-h-age">Age</span>
                            <span className="reports-gen-age-unified-h-name">Name</span>
                            <span className="reports-gen-age-unified-h-mo">Mo</span>
                            <span className="reports-gen-age-unified-h-day">Day</span>
                            <span className="reports-gen-age-unified-header-line-slot" aria-hidden="true" />
                            <span className="reports-gen-age-unified-count">Count</span>
                          </div>
                        </div>
                      ) : (
                        <div className="reports-gen-age-table-header">
                          <span className="reports-gen-age-col-gen">GEN</span>
                          <span className="reports-gen-age-header-spacer" aria-hidden="true" />
                          <span className="reports-gen-age-col-count">Count</span>
                        </div>
                      )}
                      {topOfLineName ? (
                        <div className="reports-gen-age-top">{topOfLineName}</div>
                      ) : null}
                      {reportData.generations?.map((gen) => {
                        const genMembers = getGenAgeMembersSorted(gen);
                        const isFlatGenAgeApi = Array.isArray(gen.members);
                        return (
                          <div key={gen.generation_level} className="reports-gen-age-section">
                            <div className="reports-gen-age-group-header">
                              <span className="reports-gen-age-badge">{gen.generation_level}</span>
                              <div className="reports-gen-age-divider" />
                              <span className="reports-gen-age-count-badge">{gen.total_count ?? genMembers.length ?? 0}</span>
                            </div>
                            <div className="reports-gen-age-members-container">
                              <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                                {gen.generation_level}
                              </span>
                              <div className="reports-gen-age-list-scroll">
                                {isFlatGenAgeApi ? (
                                  <>
                                    {genMembers.map((member) => {
                                      const personName = formatPersonName(member);
                                      const headShort = headNameForGenAgeLineColumn(
                                        member,
                                        gen.generation_level
                                      );
                                      const lineHead =
                                        headShort || "Start/Top of Line";
                                      const colorNm = member.color_name || "";
                                      const hex = member.hex_code || "#000000";
                                      return (
                                        <div key={member.id} className="reports-gen-age-order-row">
                                          <span className="reports-gen-age-order-cell reports-gen-age-order-age">
                                            {isReportInLawMember(member) ? "" : member.age ?? ""}
                                          </span>
                                          <span className="reports-gen-age-order-cell reports-gen-age-order-name">
                                            {personName}
                                            {renderReportNameMarkers(member, {
                                              showInLawAsterisk:
                                                isReportInLawMember(member) &&
                                                  !isExplicitDashboardStartOrTopOfLineMember(member),
                                            })}
                                          </span>
                                          <span className="reports-gen-age-order-cell reports-gen-age-order-mo">
                                            {member.birth_month != null && member.birth_month !== ""
                                              ? member.birth_month
                                              : "—"}
                                          </span>
                                          <span className="reports-gen-age-order-cell reports-gen-age-order-day">
                                            {member.birth_date != null && member.birth_date !== ""
                                              ? member.birth_date
                                              : "—"}
                                          </span>
                                          <div className="reports-gen-age-order-cell reports-gen-age-order-line-cell">
                                            {renderNameWithColor(
                                              lineHead,
                                              hex,
                                              colorNm ? `${colorNm} Line` : ""
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </>
                                ) : (
                                  <>
                                    <div className="reports-gen-age-age-label">Age</div>
                                    {genMembers.map((member) => {
                                      const cg = member._cg;
                                      return renderReportMemberRow(member, {
                                        headOfLineName:
                                          getHeadOfLineNameFromGroup(cg) || cg.head_full_name || cg.color_name,
                                        hexCode: member.hex_code || cg.hex_code,
                                        colorName: member.color_name || cg.color_name,
                                        key: member.id,
                                        showAge: true,
                                        age: member.age,
                                      });
                                    })}
                                  </>
                                )}
                              </div>
                              <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                                {gen.total_count ?? genMembers.length ?? 0}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : isAgeOrderReport ? (
                  <div className="reports-gen-age">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">{reportData.title || "Descendants Only"}</span>
                      <span className="reports-gen-age-subtitle">{reportData.subtitle || "Family in Age Order"}</span>
                      <span className="reports-gen-age-year">{reportData.year ?? new Date().getFullYear()}</span>
                    </div>
                    <div className="reports-gen-age-body">
                      <div className="reports-gen-age-table-header">
                        <span className="reports-gen-age-col-gen">GEN</span>
                        <span className="reports-gen-age-header-spacer" aria-hidden="true" />
                        <span className="reports-gen-age-col-count">Count</span>
                      </div>
                      {(() => {
                        const members = reportData.members || [];
                        const byGen = members.reduce((acc, m) => {
                          const gen = m.generation_level ?? 0;
                          if (!acc[gen]) acc[gen] = [];
                          acc[gen].push(m);
                          return acc;
                        }, {});
                        const genLevels = Object.keys(byGen).map(Number).sort((a, b) => a - b);
                        return genLevels.map((genLevel) => {
                          const genMembers = (byGen[genLevel] || []).sort((a, b) => (b.age ?? 0) - (a.age ?? 0));
                          return (
                            <div key={genLevel} className="reports-gen-age-section">
                              <div className="reports-gen-age-group-header">
                                <span className="reports-gen-age-badge">{genLevel}</span>
                                <div className="reports-gen-age-divider" />
                                <span className="reports-gen-age-count-badge">{genMembers.length}</span>
                              </div>
                              <div className="reports-gen-age-members-container">
                                <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                                  {genLevel}
                                </span>
                                <div className="reports-gen-age-list-scroll">
                                  <div className="reports-gen-age-age-label">Age</div>
                                  {genMembers.map((member) =>
                                    renderReportMemberRow(member, {
                                      headOfLineName: getHeadOfLineName(member) || "Start/Top of Line",
                                      hexCode: member.hex_code,
                                      colorName: member.color ?? member.color_name,
                                      key: member.id,
                                      showAge: true,
                                      age: member.age,
                                    })
                                  )}
                                </div>
                                <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                                  {genMembers.length}
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : isDeceasedByMonthReport ? (
                  <div className="reports-gen-age reports-deceased-by-month">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">{reportData.title || "Deceased by Month"}</span>
                      <span className="reports-gen-age-subtitle">{reportData.subtitle || "Includes In-Laws"}</span>
                      <span className="reports-gen-age-year">{reportData.year ?? new Date().getFullYear()}</span>
                    </div>
                    <div className="reports-gen-age-body">
                      <div className="reports-gen-age-members-container reports-deceased-header-row reports-deceased-header-row--one-line">
                        <span className="reports-deceased-header-side-label">Mo</span>
                        <div className="reports-gen-age-list-scroll">
                          <div className="reports-deceased-table-header">
                            <span className="reports-deceased-h-day">Day</span>
                            <span className="reports-deceased-h-name-slot" aria-hidden="true" />
                            <span className="reports-deceased-h-year">Year</span>
                            <span className="reports-deceased-h-line">Line</span>
                            <span className="reports-deceased-h-time">Time Deceased</span>
                          </div>
                        </div>
                        <span className="reports-deceased-header-side-label">Count</span>
                      </div>
                      {deceasedByMonthMonths.map(({ month, count, members }) => (
                        <div key={month} className="reports-gen-age-section">
                          <div className="reports-gen-age-group-header reports-deceased-group-header">
                            <span className="reports-gen-age-badge">{month}</span>
                            <span className="reports-deceased-divider-spacer" aria-hidden="true" />
                            <span className="reports-gen-age-count-badge">{count}</span>
                          </div>
                          <div className="reports-gen-age-members-container">
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {month}
                            </span>
                            <div className="reports-gen-age-list-scroll">
                              {members.map((m) => renderDeceasedByMonthRow(m))}
                            </div>
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : isDeceasedByYearReport ? (
                  <div className="reports-gen-age reports-deceased-by-month reports-deceased-by-year">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">{reportData.title || "Deceased by Year"}</span>
                      <span className="reports-gen-age-subtitle">{reportData.subtitle || "Includes In-Laws"}</span>
                      <span className="reports-gen-age-year">{reportData.year ?? new Date().getFullYear()}</span>
                    </div>
                    <div className="reports-gen-age-body">
                      <div className="reports-gen-age-members-container reports-deceased-header-row reports-deceased-header-row--one-line">
                        <span className="reports-deceased-header-side-label">Yr</span>
                        <div className="reports-gen-age-list-scroll">
                          <div className="reports-deceased-table-header reports-deceased-table-header--no-day">
                            <span className="reports-deceased-h-name-slot" aria-hidden="true" />
                            <span className="reports-deceased-h-year">Year</span>
                            <span className="reports-deceased-h-line">Line</span>
                            <span className="reports-deceased-h-time">Time Deceased</span>
                          </div>
                        </div>
                        <span className="reports-deceased-header-side-label">Count</span>
                      </div>
                      {deceasedByYearGroups.map(({ year, count, members }) => (
                        <div key={year} className="reports-gen-age-section">
                          <div className="reports-gen-age-group-header reports-deceased-group-header">
                            <span className="reports-gen-age-badge">{year}</span>
                            <span className="reports-deceased-divider-spacer" aria-hidden="true" />
                            <span className="reports-gen-age-count-badge">{count}</span>
                          </div>
                          <div className="reports-gen-age-members-container">
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {year}
                            </span>
                            <div className="reports-gen-age-list-scroll">
                              {members.map((m) => renderDeceasedMemberRow(m))}
                            </div>
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : isFamilyMembersByYearReport ? (
                  <div className="reports-gen-age reports-year-of-birth">
                    <div className="reports-gen-age-header">
                      <span className="reports-gen-age-title">{reportData.title || "Includes Spouses"}</span>
                      <span className="reports-gen-age-subtitle">{reportData.subtitle || "New Family Members by Year of Birth"}</span>
                      <span className="reports-gen-age-year">
                        {`${familyMembersSinceYear} - ${new Date().getFullYear()}`}
                      </span>
                    </div>
                    <div className="reports-gen-age-body">
                      <div className="reports-gen-age-unified-header-wrap">
                        <div className="reports-gen-age-unified-header-single-line">
                          <span className="reports-gen-age-unified-gen">Yr</span>
                          <span className="reports-gen-age-unified-header-gutter" aria-hidden="true" />
                          <span className="reports-gen-age-unified-h-age">Age</span>
                          <span className="reports-gen-age-unified-h-name">Name</span>
                          <span className="reports-gen-age-unified-h-mo">Mo</span>
                          <span className="reports-gen-age-unified-h-day">Day</span>
                          <span className="reports-gen-age-unified-header-line-slot" aria-hidden="true" />
                          <span className="reports-gen-age-unified-count">Count</span>
                        </div>
                      </div>
                      {familyMembersByYearGroups.map(({ year, count, members }) => (
                        <div key={year} className="reports-gen-age-section">
                          <div className="reports-gen-age-group-header">
                            <span className="reports-gen-age-badge">{year}</span>
                            <div className="reports-gen-age-divider" />
                            <span className="reports-gen-age-count-badge">{count}</span>
                          </div>
                          <div className="reports-gen-age-members-container">
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {year}
                            </span>
                            <div className="reports-gen-age-list-scroll">
                              {members.map((member, idx) =>
                                renderFamilyMemberByYearRow(
                                  member,
                                  member.id || `year-${year}-${idx}`
                                )
                              )}
                            </div>
                            <span className="reports-gen-age-spacer-badge" aria-hidden="true">
                              {count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : isAdultFamilyMembersReport ? (
                  <div className="reports-adult-members">
                    <div className="reports-adult-header">
                      <h4 className="reports-adult-title">18 + Family Members (Desc)</h4>
                      <span className="reports-adult-count">{reportData.total_count ?? 0}</span>
                    </div>
                    <div className="reports-adult-members-list-scroll">
                      <div className="reports-adult-members-grid">
                        {sortedAdultMembers.map((member) => renderAdultMemberRow(member))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="reports-data">
                    <h4 className="reports-data-title">Report Data</h4>
                    <pre className="reports-data-content">
                      {JSON.stringify(reportData, null, 2)}
                    </pre>
                  </div>
                )}
                {/* Same footer legend as the dashboard detail pages, under every
                    laid-out report — angel, in-law asterisk and multiple-birth
                    tags — so each report keys its own markers. The multiple-birth
                    entry narrows to the set sizes the report holds, as there. */}
                {isLaidOutReport && (
                  <div className="reports-page in-law-footer">
                    <DashboardMarkerLegend
                      multipleBirthTags={getMultipleBirthLegendTags(reportLegendMembers)}
                    />
                  </div>
                )}
              </>
            )}
            
            {!loading && !error && !reportData && selectedReportType && (
              <div className="reports-empty">
                <p>Select a report type to view data</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
